import { ChangeDetectorRef, Component, forwardRef, Inject, Input, OnInit, Optional, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CronOptions } from '../cron-editor/CronOptions';
import { CronFlavor } from '../cron-editor/enums';
import { CronGenComponent } from '../cron-editor/cron-editor.component';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs/internal/Subscription';
import { HttpService } from '../../utility/http-service';
@Component({
  standalone: true,
  selector: 'app-cron-schedule-editor',
  templateUrl: 'cron-schedule-editor.component.html',
  styleUrl: 'cron-schedule-editor.component.css',
  imports: [
    CommonModule,
    FormsModule,
    CronGenComponent
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CronGenComponent),
      multi: true,
    }
  ]
})
export class CronScheduleEditorComponent implements OnInit {
  @Input() clientId?: string;
  @Input() title: string = '';
  @Input() currentScheduleEndpoint: string = 'ClientSettings/CronText';
  @Input() saveScheduleEndpoint: string = 'ClientSettings/SaveSchedule';
  @Input() deleteScheduleEndpoint: string = 'ClientSettings/RemoveSchedule';
  @Input() syncEndpoint: string = 'ClientSettings/RunSync';
  @Input() jobType: string = '';
  @Input() jobKey?: string;

  private routeQueryParamsSubscription?: Subscription;
  public cronExpression = '0 0 1/1 * *';
  public isCronDisabled = false;
  public cronOptions: CronOptions = {
    formInputClass: 'form-control cron-editor-input',
    formSelectClass: 'form-control cron-editor-select',
    formRadioClass: 'cron-editor-radio',
    formCheckboxClass: 'cron-editor-checkbox',

    defaultTime: '00:00:00',

    hideMinutesTab: true,
    hideHourlyTab: true,
    hideDailyTab: false,
    hideWeeklyTab: true,
    hideMonthlyTab: false,
    hideYearlyTab: true,
    hideAdvancedTab: true,
    hideSpecificWeekDayTab: true,
    hideSpecificMonthWeekTab: true,

    use24HourTime: true,
    hideSeconds: false,

    cronFlavor: CronFlavor.standard
  };

  cronFlavorValues = CronFlavor;
  cronFlavorKeys: string[];

  @ViewChild('cronEditorDemo', { static: false })
  cronEditorDemo?: CronGenComponent;

  constructor(
    @Optional() public dialogRef: MatDialogRef<ScheduleProperties>,
    @Optional() @Inject(MAT_DIALOG_DATA) public model: ScheduleProperties,
    private route: ActivatedRoute,
    private http: HttpService,
    private cdr: ChangeDetectorRef
  ) {
    this.cronFlavorKeys = Object.keys(this.cronFlavorValues);
  }

  cronFlavorChange() {
    if (this.cronEditorDemo) {
      this.cronEditorDemo.options = this.cronOptions;
      this.cronEditorDemo.regenerateCron();
    }
  }

  ngOnInit(): void {
    // For modal usage: fall back to model.queryParms
    const queryParms = this.buildQueryParams();
    const baseUrl = this.model?.currentScheduleEndpoint ?? this.currentScheduleEndpoint;

    // Watch for manual cron override in query params
    this.routeQueryParamsSubscription = this.route.queryParams.subscribe((params: any) => {
      if (params['cron'] && params['cron'].length > 0) {
        this.cronExpression = params['cron'];
      }
    });

    // Load existing schedule
    const url = this.createUrl(baseUrl, queryParms);
    this.http.get(url, (result: CronResponse) => {
      this.cronExpression = result?.cronValue ?? '';
      if (this.cronEditorDemo) {
        this.cronEditorDemo.handleModelChange(this.cronExpression);
      }
      this.cdr.detectChanges();
    });
  }


  createUrl(baseUrl: string, queryParms?: { [key: string]: string }): string {
    let url = baseUrl;
    if (!queryParms) return url;
    const keys = Object.keys(queryParms);
    if (keys.length === 0) return url;

    url += '?' + keys.map(k => `${k}=${queryParms[k]}`).join('&');
    return url;
  }


  ngOnDestroy(): void {
    if (this.routeQueryParamsSubscription) {
      this.routeQueryParamsSubscription.unsubscribe();
    }
  }

  private buildQueryParams(): { [key: string]: string } {
    return {
      clientId: this.clientId ?? '',
      jobType: this.jobType,
      ...(this.jobKey ? { jobKey: this.jobKey } : {}),
      ...(this.model?.queryParms ?? {})
    };
  }

  onSubmit() {
    const queryParms = this.buildQueryParams();
    const formData = new FormData();
    formData.append('cron', this.cronExpression);
    for (let key in queryParms) formData.append(key, queryParms[key]);

    this.http.post(this.model?.saveScheduleEndpoint ?? this.saveScheduleEndpoint,
      () => { if (this.dialogRef) this.dialogRef.close(); },
      null, formData);
  }

  onDelete() {
    const queryParms = this.buildQueryParams();
    const url = this.createUrl(this.model?.deleteScheduleEndpoint ?? this.deleteScheduleEndpoint, queryParms);
    this.http.post(url, () => { });
  }

  onCancel() {
    if (this.dialogRef) this.dialogRef.close();
  }

  onSync() {
    const queryParms = this.buildQueryParams();
    const url = this.createUrl(this.model?.syncEndpoint ?? this.syncEndpoint, queryParms);
    this.http.post(url, () => { });
  }


}

export declare interface ScheduleProperties {
  
  currentScheduleEndpoint: string;
  saveScheduleEndpoint: string;
  deleteScheduleEndpoint: string;
  syncEndpoint: string;
  title: string;
  queryParms: { [key: string]: string };
}

export declare interface CronResponse {
  cronValue?: string;
}
